import { FC } from 'react'
import { useSelector } from 'react-redux'
import useActionGuard from 'hooks/useActionGuard'
import { PostPrivacy } from '@wellesley/openapi'
import { ButtonLoader, cn, TooltipWrapper } from '@wellesley/ui'
import { uploadsProgressSlice } from '@wellesley/uploads'

import CharsCounter from '../CharsCounter'
import getPrivacyOptions from '../helpers/privacy/getPrivacyOptions'
import useOpenContentWarning from '../hooks/useOpenContentWarning'
import useOpenLocationInput from '../hooks/useOpenLocationInput'
import checkPrivacyOptionVariant from '../helpers/privacy/checkPrivacyOptionVariant'
import AddMedia, { type TAddMedia } from '../AddMedia'
import { EmojiDropdown, TEmojiDropdown } from '../../../EmojiDropdown'
import { PrivacyOptionsVariant, TPostForm } from '../types'

import PollControl from './PollControl'
import { PrivacyDropdown } from './PrivacyDropdown'
import { TPrivacyDropdown } from './PrivacyDropdown/types'
import { AuthorIdentitySelector } from './AuthorIdentitySelector'
import SensitiveControl, {
  type SensitiveControlProps,
} from './SensitiveControl'
import LocationSelector, {
  type LocationSelectorProps,
} from './LocationSelector'

type ControlsProps = TEmojiDropdown &
  Omit<TAddMedia, 'isDisabled'> &
  Omit<TPrivacyDropdown, 'privacyOptions'> &
  Omit<
    SensitiveControlProps,
    'isOpenContentWarning' | 'toggleOpenContentWarning'
  > &
  Omit<
    LocationSelectorProps,
    'isOpenLocationInput' | 'toggleOpenLocationInput'
  > &
  Pick<
    TPostForm,
    | 'feed'
    | 'isReply'
    | 'isPrivacy'
    | 'isCompactField'
    | 'submitButtonText'
    | 'isDisabledCommentOption'
    | 'isModal'
    | 'authorIdentityOptions'
  > & {
    text: string
    isLoading: boolean
    isPollStep: boolean
    isSensitive: boolean
    isValidText: boolean
    canSubmitPost: boolean
    isDisabledMedia: boolean
    authorIdentityId?: string | null
    privacyOptionsVariant?: PrivacyOptionsVariant
    onAuthorIdentityChange?: (this: void, value: string | null) => void
    onSubmit: (this: void) => void
    setPrivacy: (this: void, privacy: PostPrivacy) => void
    togglePollStep: (this: void) => void
  }

const ControlsBar: FC<ControlsProps> = (props) => {
  const {
    text,
    feed,
    privacy,
    isReply,
    uploads,
    isLoading,
    isPollStep,
    isSensitive,
    isValidText,
    canSubmitPost,
    contentWarning,
    submitButtonText,
    isAllowedComments,
    isPrivacy = true,
    isDisabledCommentOption = true,
    isModal,
    authorIdentityOptions,
    authorIdentityId,
    privacyOptionsVariant,
    onAuthorIdentityChange,
    onSubmit,
    setPrivacy,
    onAddMedia,
    onEmojiClick,
    togglePollStep,
    onAllowComments,
    toggleSensitive,
    setContentWarning,
    location,
    setLocation,
    clearLocation,
  } = props

  const hasUploads = uploads.length > 0
  const hasUploadError = useSelector(
    uploadsProgressSlice.selectors.selectHasError,
  )
  const { isOpenContentWarning, toggleOpenContentWarning } =
    useOpenContentWarning(hasUploads)
  const { isOpenLocationInput, toggleOpenLocationInput } =
    useOpenLocationInput()

  const { isDisabled, ActionTooltipWrapper } = useActionGuard()

  const checkedPrivacyOptionsVariant = checkPrivacyOptionVariant(
    privacyOptionsVariant,
    isReply,
  )

  const privacyOptions = getPrivacyOptions({
    variant: checkedPrivacyOptionsVariant,
  })

  const isAnyInputOpen = isOpenContentWarning || isOpenLocationInput

  return (
    <div
      id="post-form-controls-bar"
      className={cn('flex w-full items-center justify-between bg-white', {
        'pb-m': !props.isReply,
      })}
    >
      <div className="gap-s flex flex-1">
        {!isAnyInputOpen && (
          <div className="flex items-center gap-4">
            <TooltipWrapper
              tooltipText={
                isPollStep ? "Polls and media can't be used together" : ''
              }
              align="start"
              theme="dark"
              size="small"
            >
              <AddMedia
                uploads={uploads}
                onAddMedia={onAddMedia}
                isDisabled={!!isPollStep}
              />
            </TooltipWrapper>
            <EmojiDropdown onEmojiClick={onEmojiClick} />
            {isPrivacy && (
              <PrivacyDropdown
                onPrivacyChange={setPrivacy}
                {...{
                  privacy: privacy as PostPrivacy,
                  privacyOptions,
                  isAllowedComments,
                  onAllowComments,
                  isDisabledCommentOption,
                }}
                isReply={isReply || (feed && feed.id !== feed.conversationId)}
              />
            )}
            <PollControl
              isPollPost={isPollStep}
              isDisabled={hasUploads}
              togglePollStep={togglePollStep}
            />
          </div>
        )}
        {hasUploads && !isOpenLocationInput && (
          <SensitiveControl
            isSensitive={!!isSensitive}
            isDisabled={hasUploadError}
            {...{
              toggleSensitive,
              contentWarning,
              setContentWarning,
              isOpenContentWarning,
              toggleOpenContentWarning,
            }}
          />
        )}
        {!isOpenContentWarning && (
          <LocationSelector
            {...{
              location,
              setLocation,
              clearLocation,
              isOpenLocationInput,
              toggleOpenLocationInput,
            }}
          />
        )}
      </div>
      {!isAnyInputOpen && (
        <div className="gap-xxs flex items-center">
          {authorIdentityOptions && isModal && onAuthorIdentityChange && (
            <AuthorIdentitySelector
              options={authorIdentityOptions}
              selectedValue={authorIdentityId}
              onValueChange={onAuthorIdentityChange}
            />
          )}
          <CharsCounter text={text} />
          <ActionTooltipWrapper>
            <TooltipWrapper
              tooltipText={!isValidText ? 'Too many characters' : ''}
              align="end"
              theme="dark"
              size="small"
            >
              <ButtonLoader
                type="submit"
                size="m"
                variant="primary"
                data-testid="submit-post"
                disabled={!canSubmitPost || isDisabled || hasUploadError}
                isLoading={isLoading}
                onClick={onSubmit}
              >
                {submitButtonText}
              </ButtonLoader>
            </TooltipWrapper>
          </ActionTooltipWrapper>
        </div>
      )}
    </div>
  )
}

export default ControlsBar
